import React, { useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
} from 'react-native';
import { DbSwipeActionProps, DbSwipeActionRef, SwipeActionButton } from './DbSwipeAction.types';
import { styles } from './DbSwipeAction.styles';
import { useSwipeListContext } from './SwipeContext';
// @ts-ignore
import SwipeRow from './components/SwipeRow.jsx';

// 用于生成唯一 ID 的计数器
let swipeIdCounter = 0;

/**
 * DbSwipeAction 左滑操作组件
 *
 * v3 重构：基于 react-native-swipe-list-view 的 SwipeRow 封装
 * - 使用 SwipeRow 的 setScrollEnabled 机制解决列表内手势冲突
 * - 通过 SwipeListContext 自动获取父级列表的滚动控制能力
 * - 支持多个 SwipeAction 之间的互斥（打开一个时自动关闭其他）
 * - 保持 DbSwipeAction 原有 API 不变
 * - 支持在 FlatList / ScrollView 列表中正常左滑
 */
export const DbSwipeAction = forwardRef<DbSwipeActionRef, DbSwipeActionProps>(({
    children,
    rightActions = [],
    leftActions = [],
    disabled = false,
    threshold: _threshold = 20,
    velocityThreshold: _velocityThreshold = 0.3,
    actionWidth = 80,
    autoClose = true,
    onOpenChange,
    style,
    contentStyle,
    onPress,
    swipeId: propsSwipeId,
    exclusive = true, // 默认开启互斥模式
}, ref) => {
    const swipeRowRef = useRef<any>(null);
    const swipeListContext = useSwipeListContext();
    const isOpenRef = useRef(false);
    
    // 生成或使用外部传入的唯一 ID
    const swipeIdRef = useRef(propsSwipeId || `swipe-${++swipeIdCounter}`);
    const swipeId = swipeIdRef.current;

    // 计算左右操作按钮总宽度
    const rw = rightActions.reduce((s, a) => s + (a.width || actionWidth), 0);
    const lw = leftActions.reduce((s, a) => s + (a.width || actionWidth), 0);

    // 关闭当前行的函数
    const closeRow = useCallback(() => {
        swipeRowRef.current?.closeRow();
    }, []);

    // 注册到上下文（用于互斥关闭）
    useEffect(() => {
        if (exclusive && swipeListContext) {
            swipeListContext.registerSwipeable(swipeId, closeRow);
            return () => {
                swipeListContext.unregisterSwipeable(swipeId);
            };
        }
    }, [exclusive, swipeId, swipeListContext, closeRow]);

    // 暴露 ref 方法
    useImperativeHandle(ref, () => ({
        close: closeRow,
        openLeft: () => lw > 0 && swipeRowRef.current?.manuallySwipeRow(lw),
        openRight: () => rw > 0 && swipeRowRef.current?.manuallySwipeRow(-rw),
    }), [rw, lw, closeRow]);

    // Action 按钮点击
    const handleActionPress = useCallback((action: SwipeActionButton) => {
        action.onPress?.();
        if (autoClose) {
            closeRow();
        }
    }, [autoClose, closeRow]);

    // 控制父级列表滚动
    const handleSetScrollEnabled = useCallback((enabled: boolean) => {
        swipeListContext?.setScrollEnabled(enabled);
    }, [swipeListContext]);

    // 渲染单个操作按钮
    const renderActionButton = (action: SwipeActionButton) => {
        const buttonWidth = action.width || actionWidth;
        return (
            <TouchableOpacity
                key={action.key}
                activeOpacity={0.8}
                onPress={() => handleActionPress(action)}
                style={[
                    styles.actionButton,
                    { width: buttonWidth, backgroundColor: action.backgroundColor || '#C7C7CC' },
                    action.style,
                ]}
            >
                {action.icon && <View style={styles.actionIcon}>{action.icon}</View>}
                {action.text && (
                    <Text
                        style={[styles.actionText, { color: action.color || '#FFFFFF' }, action.textStyle]}
                        numberOfLines={1}
                    >
                        {action.text}
                    </Text>
                )}
            </TouchableOpacity>
        );
    };

    // 渲染隐藏层（操作按钮区域）
    const renderHiddenRow = () => (
        <View style={[styles.hiddenContainer]}>
            {/* 左侧操作按钮（右滑显示） */}
            {leftActions.length > 0 && (
                <View style={[styles.leftHiddenActions, { width: lw }]}>
                    {leftActions.map(renderActionButton)}
                </View>
            )}
            {/* 右侧操作按钮（左滑显示）- 靠右对齐 */}
            {rightActions.length > 0 && (
                <View style={[styles.rightHiddenActions, { width: rw }]}>
                    {rightActions.map(renderActionButton)}
                </View>
            )}
        </View>
    );

    // 渲染可见内容
    const renderVisibleRow = () => (
        <View style={contentStyle}>
            {children}
        </View>
    );

    return (
        <SwipeRow
            ref={swipeRowRef}
            style={[styles.container, style]}
            leftOpenValue={lw}
            rightOpenValue={-rw}
            stopLeftSwipe={lw > 0 ? lw : 0}
            stopRightSwipe={rw > 0 ? -rw : 0}
            disableLeftSwipe={disabled || rw === 0}
            disableRightSwipe={disabled || lw === 0}
            disableHiddenLayoutCalculation={true}
            closeOnRowPress={true}
            useNativeDriver={true}
            directionalDistanceChangeThreshold={2}
            swipeToOpenPercent={50}
            swipeToClosePercent={50}
            friction={10}
            tension={68}
            setScrollEnabled={handleSetScrollEnabled}
            swipeGestureBegan={() => {
                // 滑动开始时立即关闭其他项
                if (exclusive && swipeListContext) {
                    swipeListContext.closeOthers(swipeId);
                }
            }}
            onRowOpen={(toValue: number) => {
                isOpenRef.current = true;
                onOpenChange?.(true, toValue > 0 ? 'left' : 'right');
            }}
            onRowDidClose={() => {
                isOpenRef.current = false;
                onOpenChange?.(false, null);
            }}
            onRowPress={() => {
                onPress?.();
            }}
        >
            {/* SwipeRow 要求第一个子元素是隐藏层，第二个是可见层 */}
            {renderHiddenRow()}
            {renderVisibleRow()}
        </SwipeRow>
    );
});

DbSwipeAction.displayName = 'DbSwipeAction';

export const SwipeAction = DbSwipeAction;
export default DbSwipeAction;
export { SwipeListProvider, SwipeListContext, useSwipeListContext } from './SwipeContext';
export type { DbSwipeActionProps, DbSwipeActionRef, SwipeActionButton } from './DbSwipeAction.types';
